import { EventType } from "@/analytics/analytics-events";
import { OpenProjectEventProperties } from "@/analytics/create-area-workflow-events-properties";
import { useSavedViewMode } from "@/features/saved-views/saved-view-context";
import { useAppSelector } from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { APP_VERSION } from "@/utils/env-constants";
import { useNonExhaustiveEffect } from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import { GUID } from "@faro-lotv/foundation";
import { isIElementAreaSection } from "@faro-lotv/ielement-types";
import {
  selectAllIElementsOfType,
  selectCompanyId,
  selectIsProjectSnapshot,
} from "@faro-lotv/project-source";
import {
  ActivityActionType,
  ActivityResultType,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { isEqual } from "es-toolkit";
import { useClientId } from "./project-access-provider";

/**
 * Tracks the analytics for opening a project
 *
 * @param isProjectReady whether the project is loaded
 */
export function useTrackOpenProject(isProjectReady: boolean): void {
  const customMode = useSavedViewMode();

  const allAreas = useAppSelector(
    selectAllIElementsOfType(isIElementAreaSection),
    isEqual,
  );

  const isSnapshot = useAppSelector(selectIsProjectSnapshot);

  // Should only log once per project load
  useNonExhaustiveEffect(() => {
    if (isProjectReady) {
      Analytics.track<OpenProjectEventProperties>(EventType.openProject, {
        isEmpty: allAreas.length === 0,
        mode: customMode ?? "default",
        isSnapshot,
      });
    }
  }, [isProjectReady, isSnapshot]);
}

/**
 * Publishes an audit activity event to the Activity Tracking API when the
 * project is opened.
 *
 * This is used for auditing and compliance purposes, allowing customers to track
 * when a user has opened one of their projects.
 *
 * @param projectId the ID of the opened project
 * @param isProjectReady whether the project is loaded
 */
export function useTrackProjectViewedAuditEvent(
  projectId: GUID,
  isProjectReady: boolean,
): void {
  const { activityApiClient } = useApiClientContext();
  const currentUser = useAppSelector(selectCurrentUser);
  const companyId = useAppSelector(selectCompanyId);
  const { clientId } = useClientId();

  // Should only publish once per project load
  useNonExhaustiveEffect(() => {
    if (!isProjectReady || !activityApiClient || !currentUser?.id) return;

    // Ignore any errors, as this is just an auditing event and should not prevent the user from accessing their project.
    activityApiClient.publishActivityEvent({
      action: {
        type: ActivityActionType.viewProject,
        data: { projectId, userId: currentUser.id },
      },
      result: { type: ActivityResultType.projectViewed },
      timestamp: new Date().toISOString(),
      source: {
        type: clientId,
        version: APP_VERSION,
      },
      context: {
        projectId,
        companyId,
        userId: currentUser.id,
        userEmail: currentUser.email,
      },
    });
  }, [isProjectReady]);
}
